/**
 * The money ledger.
 *
 * Same blueprint as the other modules, with one thing peculiar to this one: the status
 * column does not tell you what happened to the money. A PARTIAL refund leaves the payment
 * `CAPTURED` — the amount goes back, the status does not move — so a row reading "Taken"
 * may have had half its value returned, and any revenue figure that trusts the status is
 * wrong by exactly that much. Every row therefore states what the money actually did, and
 * part-refunds are called out as their own thing.
 *
 * The other number this screen exists to surface is PENDING. Those are not payments; they
 * are payments that never happened — a customer who started a checkout and walked away.
 * Counting them as revenue is the easiest mistake on this page, so they are separated out
 * with their own total.
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import {
  AlertTriangle,
  Banknote,
  Copy,
  CreditCard,
  IndianRupee,
  MoreHorizontal,
  RotateCcw,
  Store,
  Undo2,
  Unlink,
} from 'lucide-react';
import {
  bool_param,
  enum_param,
  page_param,
  page_size_param,
  sort_param,
  string_param,
  use_url_state,
} from '@/hooks/use-url-state';
import { DashboardLayout, PageTabs } from '@/components/dashboard-layout';
import { DataTable } from '@/components/data-table/data-table';
import {
  BoolFilter,
  FilterBar,
  SearchInput,
  SelectFilter,
  type ChipInput,
} from '@/components/filters/filter-bar';
import { DateRangeFilter, describe_range } from '@/components/filters/date-range-filter';
import { SavedViews } from '@/components/filters/saved-views';
import { MetricStrip, type Metric } from '@/components/metric-strip';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { notify } from '@/components/ui/toaster';
import { ago, inr } from '@/lib/format';
import { export_payments, use_payment_counts, use_payment_list } from '@/features/payments/hooks';
import {
  PAYMENT_SORT_FIELDS,
  PAYMENT_STATUS_LABEL,
  PAYMENT_STATUS_OPTIONS,
  PAYMENT_STATUS_TONE,
  SUBJECT_LABEL,
  is_partly_refunded,
  money_story,
  type AdminPayment,
  type PaymentCounts,
  type PaymentFilters,
  type PaymentStatus,
} from '@/features/payments/types';

const SEGMENTS = ['taken', 'pending', 'refunds', 'to_settle', 'failed', 'all'] as const;
type Segment = (typeof SEGMENTS)[number];

const SEGMENT_FILTERS: Record<Segment, Partial<PaymentFilters>> = {
  taken: { status: 'CAPTURED' },
  // Not revenue. A checkout somebody started and never finished.
  pending: { status: 'PENDING' },
  // Any money back at all, partial or full — `status=REFUNDED` alone misses the partials.
  refunds: { refunded: 'true' },
  /*
   * Refunded on paper and not confirmed sent. The customer is still owed this, and before
   * the settlement column existed there was no way to ask the question at all.
   */
  to_settle: { unsettled: 'true' },
  failed: { status: 'FAILED' },
  all: {},
};

const SEGMENT_COPY: Record<Segment, { label: string; title: string; blurb: string }> = {
  taken: {
    label: 'Taken',
    title: 'Money taken',
    blurb: 'Captured payments — some of these have had part of the amount returned',
  },
  pending: {
    label: 'Never completed',
    title: 'Checkouts that never completed',
    blurb: 'A customer started paying and walked away — this is not revenue',
  },
  refunds: {
    label: 'Refunds',
    title: 'Money given back',
    blurb: 'Everything with a refund against it, whether part of the amount or all of it',
  },
  to_settle: {
    label: 'To settle',
    title: 'Refunds nobody has confirmed sending',
    blurb: 'Recorded as owed, and the money has not been marked as having left',
  },
  failed: {
    label: 'Failed',
    title: 'Payments the gateway refused',
    blurb: 'A customer tried to pay and could not',
  },
  all: { label: 'All', title: 'Payments', blurb: 'Every payment record, whatever became of it' },
};

export function PaymentListPage() {
  const [segment_raw, set_segment] = use_url_state('segment', enum_param(SEGMENTS));
  const segment: Segment = segment_raw || 'taken';
  const copy = SEGMENT_COPY[segment];

  const [q, set_q] = use_url_state('q', string_param);
  const [status, set_status] = use_url_state('status', enum_param(PAYMENT_STATUS_OPTIONS));
  const [is_dummy, set_is_dummy] = use_url_state('is_dummy', bool_param);
  const [partial, set_partial] = use_url_state('partially_refunded', bool_param);
  const [orphaned, set_orphaned] = use_url_state('orphaned', bool_param);
  const [unsettled, set_unsettled] = use_url_state('unsettled', bool_param);
  const [from, set_from] = use_url_state('from', string_param);
  const [to, set_to] = use_url_state('to', string_param);
  const [page, set_page] = use_url_state('page', page_param);
  const [page_size, set_page_size] = use_url_state('page_size', page_size_param);
  const [sort, set_sort] = use_url_state(
    'sort',
    sort_param(PAYMENT_SORT_FIELDS, { by: 'created_at', dir: 'desc' }),
  );

  const reset_page = () => set_page(1);

  const filters: PaymentFilters = useMemo(
    () => ({
      ...SEGMENT_FILTERS[segment],
      page,
      page_size,
      q: q || undefined,
      // A hand-picked status beats the segment's.
      ...(status ? { status } : {}),
      is_dummy: is_dummy || undefined,
      partially_refunded: partial || undefined,
      orphaned: orphaned || undefined,
      unsettled: unsettled || SEGMENT_FILTERS[segment].unsettled,
      from: from || undefined,
      to: to || undefined,
    }),
    [segment, page, page_size, q, status, is_dummy, partial, orphaned, unsettled, from, to],
  );

  const { data, isLoading, isFetching, error, refetch } = use_payment_list(filters, sort);
  const { data: counts } = use_payment_counts();

  /* --------------------------------- chips --------------------------------- */

  const bool_chip = (
    key: string,
    label: string,
    value: string,
    yes: string,
    no: string,
    clear: () => void,
  ) =>
    value
      ? {
          key,
          label,
          value: value === 'true' ? yes : no,
          advanced: true,
          on_clear: () => {
            clear();
            reset_page();
          },
        }
      : null;

  const chips: ChipInput[] = [
    q && { key: 'q', label: 'Reference', value: q, on_clear: () => set_q('') },
    status && {
      key: 'status',
      label: 'Status',
      value: PAYMENT_STATUS_LABEL[status as PaymentStatus],
      advanced: true,
      on_clear: () => set_status(''),
    },
    bool_chip('is_dummy', 'Kind', is_dummy, 'Test payments', 'Real money', () => set_is_dummy('')),
    bool_chip('partially_refunded', 'Refund', partial, 'Part of it back', 'Not partly back', () =>
      set_partial(''),
    ),
    bool_chip('orphaned', 'Attribution', orphaned, 'Nothing behind it', 'Attributed', () =>
      set_orphaned(''),
    ),
    segment === 'to_settle'
      ? null
      : bool_chip('unsettled', 'Settlement', unsettled, 'Not confirmed sent', 'All sent', () =>
          set_unsettled(''),
        ),
    (from || to) && {
      key: 'date',
      label: 'Taken',
      value: describe_range(from, to),
      on_clear: () => {
        set_from('');
        set_to('');
      },
    },
  ];

  function clear_all() {
    set_q('');
    set_status('');
    set_is_dummy('');
    set_partial('');
    set_orphaned('');
    set_unsettled('');
    set_from('');
    set_to('');
    reset_page();
  }

  const metrics: Metric[] = useMemo(() => build_metrics(counts), [counts]);

  /* -------------------------------- columns -------------------------------- */

  const columns = useMemo<ColumnDef<AdminPayment>[]>(
    () => [
      {
        id: 'created_at',
        header: 'When',
        meta: { sort_key: 'created_at', label: 'When', csv: (p) => p.created_at },
        cell: ({ row }) => {
          const p = row.original;
          return (
            <span className="flex min-w-0 flex-col">
              <span className="whitespace-nowrap font-semibold">{ago(p.created_at)}</span>
              <span className="whitespace-nowrap text-micro text-muted-foreground">
                {p.provider}
                {p.is_dummy ? ' · test' : ''}
              </span>
            </span>
          );
        },
      },
      {
        id: 'amount_inr',
        header: 'Amount',
        meta: {
          sort_key: 'amount_inr',
          align: 'right',
          label: 'Amount',
          csv: (p) => p.amount_inr,
        },
        cell: ({ row }) => {
          const p = row.original;
          const refunded = Number(p.refunded_amount_inr);
          return (
            <span className="flex flex-col items-end">
              <span className="whitespace-nowrap font-semibold tabular">{inr(p.amount_inr)}</span>
              {refunded > 0 && (
                <span className="whitespace-nowrap text-micro text-destructive tabular">
                  −{inr(p.refunded_amount_inr)} back
                </span>
              )}
            </span>
          );
        },
      },
      {
        id: 'status',
        header: 'What happened',
        enableSorting: false,
        meta: {
          label: 'What happened',
          width: '15rem',
          csv: (p) => money_story(p),
        },
        cell: ({ row }) => {
          const p = row.original;
          return (
            <span className="flex min-w-0 flex-col gap-1">
              <span className="flex flex-wrap items-center gap-1">
                <Badge
                  tone={PAYMENT_STATUS_TONE[p.status]}
                  size="sm"
                  dot
                  data-testid={`status-${p.id}`}
                >
                  {PAYMENT_STATUS_LABEL[p.status]}
                </Badge>
                {/*
                  The state the status column has no word for. A partial refund leaves the
                  payment CAPTURED, so without this a row reading "Taken" can have had half
                  its value returned and nothing says so.
                */}
                {is_partly_refunded(p) && (
                  <Badge tone="warning" size="sm" data-testid={`partial-${p.id}`}>
                    <Undo2 className="h-3 w-3" /> Part refunded
                  </Badge>
                )}
              </span>
              <span className="truncate text-micro text-muted-foreground">{money_story(p)}</span>
            </span>
          );
        },
      },
      {
        id: 'subject',
        header: 'What for',
        enableSorting: false,
        meta: {
          label: 'What for',
          width: '16rem',
          csv: (p) => (p.subject ? `${p.subject.kind} ${p.subject.id}` : 'Nothing behind it'),
        },
        cell: ({ row }) => {
          const s = row.original.subject;
          if (!s) {
            /*
             * Money the platform took and cannot attribute. Rare, and flagged rather than
             * rendered as an empty cell — an empty cell reads as a display bug.
             */
            return (
              <Badge tone="destructive" size="sm" data-testid={`orphan-${row.original.id}`}>
                <Unlink className="h-3 w-3" /> Nothing behind it
              </Badge>
            );
          }
          return (
            <span className="flex min-w-0 flex-col">
              {s.link_path ? (
                <Link
                  to={s.link_path}
                  onClick={(e) => e.stopPropagation()}
                  className="truncate font-medium underline-offset-2 hover:text-primary hover:underline"
                >
                  {s.label}
                </Link>
              ) : (
                <span className="truncate font-medium">{s.label}</span>
              )}
              <span className="truncate text-micro text-muted-foreground">
                {SUBJECT_LABEL[s.kind]}
                {s.vendor?.business_name ? ` · ${s.vendor.business_name}` : ''}
              </span>
            </span>
          );
        },
      },
      {
        id: 'customer',
        header: 'Who paid',
        enableSorting: false,
        meta: { label: 'Who paid', csv: (p) => p.subject?.customer?.name ?? '' },
        cell: ({ row }) => {
          const c = row.original.subject?.customer;
          if (!c) return <span className="text-micro text-muted-foreground/60">—</span>;
          return (
            <span className="flex min-w-0 flex-col">
              <Link
                to={`/users/${c.id}`}
                onClick={(e) => e.stopPropagation()}
                className="truncate font-medium underline-offset-2 hover:text-primary hover:underline"
              >
                {c.name ?? 'No name on file'}
              </Link>
              <span className="truncate text-micro text-muted-foreground">{c.phone ?? '—'}</span>
            </span>
          );
        },
      },
      {
        id: 'reference',
        header: 'Gateway ref',
        enableSorting: false,
        meta: {
          label: 'Gateway ref',
          hidden_by_default: true,
          csv: (p) => p.provider_ref ?? p.order_ref ?? '',
        },
        cell: ({ row }) => (
          <span className="truncate font-mono text-micro text-muted-foreground">
            {row.original.provider_ref ?? row.original.order_ref ?? '—'}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        meta: { align: 'right', width: '1%' },
        cell: ({ row }) => {
          const p = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-control text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  aria-label="Payment actions"
                  data-testid={`row-actions-${p.id}`}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <RowAction
                  on_select={() => {
                    void navigator.clipboard.writeText(p.provider_ref ?? p.order_ref ?? p.id);
                    notify.success('Reference copied');
                  }}
                  testid={`copy-${p.id}`}
                >
                  <Copy className="h-4 w-4" /> Copy the gateway reference
                </RowAction>
                <DropdownMenuSeparator />
                {p.subject?.link_path && (
                  <RowAction
                    on_select={() => window.open(p.subject!.link_path!, '_self')}
                    testid={`open-subject-${p.id}`}
                  >
                    <CreditCard className="h-4 w-4" /> Open what it paid for
                  </RowAction>
                )}
                {p.subject?.vendor && (
                  <RowAction
                    on_select={() => window.open(`/vendors/${p.subject!.vendor!.id}`, '_self')}
                    testid={`open-vendor-${p.id}`}
                  >
                    <Store className="h-4 w-4" /> Open the vendor
                  </RowAction>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [],
  );

  return (
    <DashboardLayout
      title={copy.title}
      subtitle={copy.blurb}
      actions={<SavedViews module={`payments:${segment}`} is_filtered={chips.some(Boolean)} />}
      tabs={
        <PageTabs
          current={segment}
          on_change={(v) => {
            set_segment(v as Segment);
            reset_page();
          }}
          tabs={SEGMENTS.map((v) => ({
            value: v,
            label: SEGMENT_COPY[v].label,
            count:
              v === 'pending'
                ? counts?.pending
                : v === 'refunds'
                  ? counts?.refunded_any
                  : v === 'to_settle'
                    ? counts?.unsettled_refunds
                    : v === 'failed'
                      ? counts?.failed
                      : undefined,
          }))}
        />
      }
      stats={<MetricStrip metrics={metrics} />}
    >
      <DataTable<AdminPayment>
        columns={columns}
        data={data?.items ?? []}
        total={data?.total ?? 0}
        get_row_id={(p) => p.id}
        page={page}
        page_size={page_size}
        on_page_change={set_page}
        on_page_size_change={(n) => {
          set_page_size(n);
          reset_page();
        }}
        sort={sort}
        on_sort_change={(next) => {
          set_sort(next);
          reset_page();
        }}
        is_loading={isLoading}
        is_fetching={isFetching}
        error={error}
        on_retry={() => void refetch()}
        row_href={(p) => `/payments/${p.id}`}
        storage_key={`payments:${segment}`}
        export_name="payments"
        on_export_all={() => export_payments(filters, sort)}
        caption="Payments"
        test_id="payment-list"
        /*
         * The filter's own money, above the table. Distinct from the tiles, which describe
         * the whole book: "₹4,200 in this filter" and "₹68,865 kept overall" answer
         * different questions and neither substitutes for the other.
         */
        toolbar_extra={
          data ? (
            <span className="flex items-center gap-3 text-micro text-muted-foreground">
              <span data-testid="total-gross">
                Gross <span className="font-semibold tabular text-foreground">{inr(data.totals.gross_inr)}</span>
              </span>
              <span data-testid="total-refunded">
                Back <span className="font-semibold tabular text-foreground">{inr(data.totals.refunded_inr)}</span>
              </span>
              <span data-testid="total-net">
                Net <span className="font-semibold tabular text-foreground">{inr(data.totals.net_inr)}</span>
              </span>
            </span>
          ) : undefined
        }
        empty={{
          icon: CreditCard,
          title: chips.some(Boolean)
            ? 'No payments match these filters'
            : `No ${copy.label.toLowerCase()} payments`,
          description:
            segment === 'failed'
              ? 'Nothing has been refused by the gateway.'
              : 'Payments appear here as customers check out.',
        }}
        filters={
          <FilterBar
            chips={chips}
            on_clear_all={clear_all}
            advanced={
              <>
                <SelectFilter
                  label="Status"
                  value={status}
                  on_change={(v) => {
                    set_status(v as PaymentStatus);
                    reset_page();
                  }}
                  options={PAYMENT_STATUS_OPTIONS.map((s) => ({
                    value: s,
                    label: PAYMENT_STATUS_LABEL[s],
                  }))}
                  all_label="Any status"
                  test_id="filter-status"
                />
                <BoolFilter
                  label="Refund"
                  value={partial}
                  on_change={(v) => {
                    set_partial(v);
                    reset_page();
                  }}
                  true_label="Part of it back"
                  false_label="Not partly back"
                  test_id="filter-partial"
                />
                <BoolFilter
                  label="Settlement"
                  value={segment === 'to_settle' ? 'true' : unsettled}
                  on_change={(v) => {
                    set_unsettled(v);
                    reset_page();
                  }}
                  true_label="Not confirmed sent"
                  false_label="All sent"
                  test_id="filter-unsettled"
                />
                <BoolFilter
                  label="Attribution"
                  value={orphaned}
                  on_change={(v) => {
                    set_orphaned(v);
                    reset_page();
                  }}
                  true_label="Nothing behind it"
                  false_label="Attributed"
                  test_id="filter-orphaned"
                />
              </>
            }
          >
            <SearchInput
              value={q}
              on_change={(next) => {
                set_q(next);
                reset_page();
              }}
              label="Reference"
              placeholder="Gateway reference or payment ID…"
              test_id="filter-q"
            />
            <BoolFilter
              label="Kind"
              value={is_dummy}
              on_change={(v) => {
                set_is_dummy(v);
                reset_page();
              }}
              true_label="Test payments"
              false_label="Real money"
              any_label="Both"
              test_id="filter-is-dummy"
            />
            <DateRangeFilter
              from={from}
              to={to}
              on_change={(next) => {
                set_from(next.from);
                set_to(next.to);
                reset_page();
              }}
              label="Taken"
            />
          </FilterBar>
        }
      />
    </DashboardLayout>
  );
}

function build_metrics(counts: PaymentCounts | undefined): Metric[] {
  return [
    {
      key: 'net',
      label: 'Kept',
      value: counts ? inr(counts.net_inr) : undefined,
      hint: counts ? `${inr(counts.gross_inr)} taken` : undefined,
      icon: IndianRupee,
      loading: !counts,
    },
    {
      key: 'refunded',
      label: 'Given back',
      value: counts ? inr(counts.refunded_inr) : undefined,
      hint: counts ? `across ${counts.refunded_any} payments` : undefined,
      to: '/payments?segment=refunds',
      icon: RotateCcw,
      loading: !counts,
    },
    {
      key: 'pending',
      label: 'Never completed',
      /*
       * Not revenue, and the easiest thing on this page to mistake for it. A customer
       * started a checkout and walked away; the money never moved.
       */
      value: counts ? inr(counts.pending_inr) : undefined,
      hint: counts ? `${counts.pending} abandoned checkouts` : undefined,
      tone: counts && counts.pending > 0 ? 'warning' : 'default',
      to: '/payments?segment=pending',
      icon: Banknote,
      loading: !counts,
    },
    {
      key: 'partial',
      label: 'Part refunded',
      // Still reading as CAPTURED, because a partial refund does not move the status.
      value: counts?.partially_refunded?.toLocaleString('en-IN'),
      hint: 'Still shown as taken',
      tone: counts && counts.partially_refunded > 0 ? 'warning' : 'default',
      to: '/payments?segment=all&partially_refunded=true',
      icon: Undo2,
      loading: !counts,
    },
    {
      key: 'owed',
      label: 'Still owed',
      /*
       * Refunded on paper and not confirmed sent. Replaces an "unattributed" tile that has
       * read 0 since the console was built — this one is a queue somebody has to work.
       */
      value: counts ? inr(counts.unsettled_inr) : undefined,
      hint: counts ? `${counts.unsettled_refunds} refunds not confirmed` : undefined,
      tone: counts && counts.unsettled_refunds > 0 ? 'destructive' : 'default',
      to: '/payments?segment=to_settle',
      icon: AlertTriangle,
      loading: !counts,
    },
  ];
}

/**
 * A dropdown item that survives Radix's pointer-based selection.
 *
 * Radix selects on pointerdown/pointerup rather than click, so a plain onClick
 * fires after the menu has begun closing and the row's own navigation wins.
 */
function RowAction({
  children,
  on_select,
  testid,
}: {
  children: React.ReactNode;
  on_select: () => void;
  testid: string;
}) {
  return (
    <DropdownMenuItem
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      onSelect={on_select}
      data-testid={testid}
    >
      {children}
    </DropdownMenuItem>
  );
}
